import { useEffect, useRef, useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X, Scissors, Check, Loader2 } from "lucide-react";
import { LOCATIONS } from "../data/site";
import { useBookingMode } from "../hooks/useBookingMode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "vault_offer_state";

export const OfferFlyer = () => {
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    location: LOCATIONS[0].id,
  });
  const nameRef = useRef(null);
  const { bookProps } = useBookingMode();

  useEffect(() => {
    if (localStorage.getItem(KEY) === "done") return;
    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      setMounted(true);
      window.removeEventListener("scroll", onScroll);
    };
    const onScroll = () => {
      if (window.scrollY > 500) reveal();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setTimeout(reveal, 9000);
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (expanded) setTimeout(() => nameRef.current?.focus(), 350);
  }, [expanded]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API}/offer-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, consent: true }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setClaimed(data.code);
      localStorage.setItem(KEY, "done");
    } catch {
      setError("Couldn't send that. Check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  const dismiss = () => {
    setMounted(false);
    localStorage.setItem(KEY, "done");
  };

  if (!mounted) return null;

  return (
    <div
      data-testid="offer-flyer"
      className="fixed bottom-[80px] left-3 z-[65] flex max-w-[calc(100vw-1.5rem)] justify-start sm:bottom-6 sm:left-auto sm:right-6 sm:justify-end lg:bottom-8 lg:right-8"
    >
      <AnimatePresence mode="wait">
        {/* ── collapsed teaser ── */}
        {!expanded && (
          <m.button
            key="tab"
            type="button"
            onClick={() => setExpanded(true)}
            data-testid="offer-flyer-open"
            initial={{ y: 30, opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 14, opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="group relative flex items-center gap-2.5 overflow-hidden border border-[#c5a059]/45 bg-[#0d1219]/92 py-2 pl-2 pr-3 backdrop-blur-xl transition-colors duration-500 hover:border-[#c5a059] sm:gap-3 sm:py-3 sm:pl-3 sm:pr-5"
          >
            <m.span
              aria-hidden
              className="absolute inset-0 -z-10"
              style={{
                background:
                  "radial-gradient(120px 60px at 0% 100%, rgba(197,160,89,0.28), transparent 70%)",
              }}
              animate={{ opacity: [0.45, 1, 0.45] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <m.span
              className="flex h-8 w-8 shrink-0 items-center justify-center bg-[#c5a059] text-[#0a0d12] sm:h-10 sm:w-10"
              animate={{ rotate: [0, -12, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Scissors size={15} className="sm:hidden" />
              <Scissors size={17} className="hidden sm:block" />
            </m.span>
            <span className="text-left leading-none">
              <span className="block font-cond text-base font-extrabold uppercase tracking-tight text-[#f2ece0] sm:text-xl">
                15% off
              </span>
              <span className="mt-0.5 block text-[8px] font-semibold uppercase tracking-[0.2em] text-[#c5a059] sm:mt-1 sm:text-[9px] sm:tracking-[0.26em]">
                <span className="sm:hidden">First visit</span>
                <span className="hidden sm:inline">
                  First visit · tap to claim
                </span>
              </span>
            </span>
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                dismiss();
              }}
              data-testid="offer-flyer-dismiss"
              aria-label="Dismiss offer"
              className="ml-0.5 flex h-7 w-7 items-center justify-center text-slate-500 transition-colors hover:text-[#d8b46b] sm:ml-1 sm:h-6 sm:w-6"
            >
              <X size={13} />
            </span>
          </m.button>
        )}

        {/* ── expanded card ── */}
        {expanded && (
          <m.div
            key="card"
            initial={{ y: 24, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="steel-panel grain relative max-h-[70svh] w-[min(360px,calc(100vw-1.5rem))] overflow-y-auto overscroll-contain backdrop-blur-xl sm:max-h-[80svh] sm:w-[380px]"
          >
            <button
              type="button"
              onClick={() => (claimed ? dismiss() : setExpanded(false))}
              data-testid="offer-flyer-close"
              aria-label="Close offer"
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center text-slate-500 transition-colors hover:text-[#d8b46b]"
            >
              <X size={15} />
            </button>

            {claimed ? (
              <div className="p-7 text-center" data-testid="offer-flyer-success">
                <span className="mx-auto flex h-12 w-12 items-center justify-center bg-[#c5a059] text-[#0a0d12]">
                  <Check size={22} />
                </span>
                <h3 className="mt-5 font-cond text-2xl font-extrabold uppercase text-[#f2ece0]">
                  You're in.
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">
                  Show this code at the shop on your first visit.
                </p>
                <p
                  data-testid="offer-flyer-code"
                  className="mt-4 border border-dashed border-[#c5a059]/55 bg-[#c5a059]/10 py-3 font-cond text-2xl font-extrabold tracking-[0.12em] text-[#e2cf9e]"
                >
                  {claimed}
                </p>
                <a
                  {...bookProps(
                    LOCATIONS.find((l) => l.id === form.location) ||
                      LOCATIONS[0],
                  )}
                  data-testid="offer-flyer-book-now"
                  className="mt-5 flex min-h-[50px] items-center justify-center bg-[#c5a059] font-cond text-[12px] font-bold uppercase tracking-[0.2em] text-[#0a0d12]"
                >
                  Book my first cut
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 sm:p-7">
                <span className="label text-[10px]">New guest offer</span>
                <h3 className="mt-2 font-cond text-3xl font-extrabold uppercase leading-[0.9] text-[#f2ece0]">
                  15% off your
                  <span className="brass-text"> first visit</span>
                </h3>
                <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                  Three fields, ten seconds. We'll text your code, no spam,
                  ever.
                </p>

                <div className="mt-5 space-y-2.5">
                  <input
                    ref={nameRef}
                    required
                    value={form.full_name}
                    onChange={(e) =>
                      setForm({ ...form, full_name: e.target.value })
                    }
                    placeholder="Full name"
                    data-testid="offer-input-name"
                    className="h-12 w-full border border-white/10 bg-white/[0.03] px-4 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-[#c5a059]"
                  />
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone"
                    data-testid="offer-input-phone"
                    className="h-12 w-full border border-white/10 bg-white/[0.03] px-4 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-[#c5a059]"
                  />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email"
                    data-testid="offer-input-email"
                    className="h-12 w-full border border-white/10 bg-white/[0.03] px-4 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-[#c5a059]"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    {LOCATIONS.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setForm({ ...form, location: l.id })}
                        data-testid={`offer-location-${l.id}`}
                        className={`min-h-[44px] border font-cond text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                          form.location === l.id
                            ? "border-[#c5a059] bg-[#c5a059]/15 text-[#e2cf9e]"
                            : "border-white/10 text-slate-500"
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <p
                    data-testid="offer-flyer-error"
                    className="mt-3 text-[11px] text-red-400"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="offer-submit"
                  className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 bg-[#c5a059] font-cond text-[13px] font-bold uppercase tracking-[0.2em] text-[#0a0d12] transition-colors hover:bg-[#d8b46b] disabled:opacity-60"
                >
                  {sending && <Loader2 size={15} className="animate-spin" />}
                  Claim my 15%
                </button>
                <p className="mt-3 text-[9px] uppercase tracking-[0.14em] text-slate-400">
                  By claiming you agree to receive messages from The Vault
                  Barber Co.
                </p>
              </form>
            )}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};
